"use client"

import type React from "react"
import { useEffect, useState } from "react"
import Navbar from "../components/Navbar"
import { fetchTransactions, confirmTransaction, generateTransactionReport, submitFeedback } from "../utils/api"
import { useAuth } from "../context/AuthContext"
import "./Transactions.css"
import FeedbackModal from "./FeedbackModal"

interface Transaction {
  id: number
  product_id: number
  buyer_id: number
  seller_id: number
  status: string
  created_at: string
  product_name?: string
  buyer_username?: string
  seller_username?: string
}

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>("all")
  const { user } = useAuth()
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal]   = useState(false);
  const [errorMessage, setErrorMessage]       = useState("");

  // Determine user's role across all transactions
  const getUserRoles = () => {
    if (!user || transactions.length === 0) return { isBuyer: false, isSeller: false }

    const isBuyer = transactions.some((tx) => tx.buyer_id === user.id)
    const isSeller = transactions.some((tx) => tx.seller_id === user.id)

    return { isBuyer, isSeller }
  }

  const { isBuyer, isSeller } = getUserRoles()

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetchTransactions()
        setTransactions(response.data.transactions)
      } catch (err) {
        console.error("Failed to load transactions", err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleConfirm = async (id: number) => {
    try {
      await confirmTransaction(id)
      setTransactions((prev) => prev.map((tx) => (tx.id === id ? { ...tx, status: "paid" } : tx)))
    } catch (err) {
      console.error("Failed to confirm transaction", err)
    }
  }

  const handleGenerateReport = async (transactionId: number) => {
    try {
      const response = await generateTransactionReport(transactionId)
      const blob = new Blob([response.data], { type: "application/pdf" })
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = url
      link.download = `transaction_${transactionId}_report.pdf`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      alert("Failed to generate report.")
      console.error("Generate report error:", error)
    }
  }

  const handleFeedbackSubmit = async (rating: number, comment: string) => {
    if (!selectedTransaction || !user) return;
  
    try {
      await submitFeedback(selectedTransaction.id, rating, comment);
  
      setShowFeedbackModal(false);
      setSelectedTransaction(null);
      setShowSuccessModal(true);           // 👈 show custom success modal
    } catch (err) {
      console.error("Failed to submit feedback", err);
      setErrorMessage(
        "Error submitting feedback. You can't submit more than one feedback for the transaction!"
      );
      setShowErrorModal(true);             // 👈 show custom error modal
    }
  };
  

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "status-pending"
      case "paid":
        return "status-paid"
      case "cancelled":
        return "status-cancelled"
      default:
        return "status-default"
    }
  }

  const getUserRole = (transaction: Transaction) => {
    if (!user) return "unknown"
    return user.id === transaction.buyer_id ? "buyer" : "seller"
  }

  const filteredTransactions = transactions.filter((tx) => {
    if (filter === "all") return true
    if (filter === "buying") return user?.id === tx.buyer_id
    if (filter === "selling") return user?.id === tx.seller_id
    if (filter === "pending") return tx.status === "pending"
    if (filter === "completed") return tx.status === "paid"
    return true
  })

  const formatDate = (dateString: string) => {
    const utcWithZ = dateString.endsWith("Z") ? dateString : `${dateString}Z`
    const date = new Date(utcWithZ)
    return date.toLocaleTimeString("en-MY", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kuala_Lumpur",
    })
  }

  // Get filter options based on user's roles
  const getFilterOptions = () => {
    const options = [
      { key: "all", label: "All Transactions" },
      { key: "pending", label: "Pending" },
      { key: "completed", label: "Completed" },
    ]

    // Only show buying/selling filters if user has both roles
    if (isBuyer && isSeller) {
      options.splice(1, 0, { key: "buying", label: "Buying" }, { key: "selling", label: "Selling" })
    }

    return options
  }

  const filterOptions = getFilterOptions()

  return (
    <>
      <Navbar />

      {selectedTransaction && (
        <FeedbackModal
          show={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
        />
      )}

      <div className="transactions-container">
        <div className="container">
          <div className="transactions-header">
            <h1 className="transactions-title">My Transactions</h1>
            <p className="transactions-subtitle">
              {isBuyer && isSeller
                ? "Manage your buying and selling activities"
                : isBuyer
                  ? "Manage your purchases"
                  : "Manage your sales"}
            </p>
          </div>

          <div className="transactions-filters">
            {filterOptions.map((option) => (
              <button
                key={option.key}
                className={`filter-btn ${filter === option.key ? "active" : ""}`}
                onClick={() => setFilter(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="transactions-content">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading transactions...</p>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h3>No transactions found</h3>
                <p>
                  {filter === "all" ? "You haven't made any transactions yet." : `No ${filter} transactions found.`}
                </p>
              </div>
            ) : (
              <div className="transactions-grid">
                {filteredTransactions.map((tx) => (
                  <div key={tx.id} className="transaction-card">
                    <div className="transaction-header">
                      <div className="transaction-id">
                        <span className="id-label">Transaction</span>
                        <span className="id-value">#{tx.id}</span>
                      </div>
                      <div className={`transaction-status ${getStatusColor(tx.status)}`}>
                        {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                      </div>
                    </div>

                    <div className="transaction-details">
                      <div className="detail-row">
                        <span className="detail-label">Product ID:</span>
                        <span className="detail-value">#{tx.product_id}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Your Role:</span>
                        <span className={`detail-value role-${getUserRole(tx)}`}>
                          {getUserRole(tx).charAt(0).toUpperCase() + getUserRole(tx).slice(1)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">{getUserRole(tx) === "buyer" ? "Seller:" : "Buyer:"}</span>
                        <span className="detail-value">
                          {getUserRole(tx) === "buyer" ? `${tx.seller_username}` : `${tx.buyer_username}`}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Created:</span>
                        <span className="detail-value">{formatDate(tx.created_at)}</span>
                      </div>
                    </div>

                    <div className="transaction-actions">
                      {user?.id === tx.seller_id && tx.status === "pending" && (
                        <button className="action-btn confirm-btn" onClick={() => handleConfirm(tx.id)}>
                          <span className="btn-icon">✓</span>
                          Confirm Payment
                        </button>
                      )}

                      {tx.status === "paid" && (
                        <div className="completed-actions">
                          <button className="action-btn report-btn" onClick={() => handleGenerateReport(tx.id)}>
                            <span className="btn-icon">📄</span>
                            Generate Report
                          </button>

                          <button
                            className="action-btn feedback-btn"
                            onClick={() => {
                              setSelectedTransaction(tx)
                              setShowFeedbackModal(true)
                            }}
                          >
                            <span className="btn-icon">⭐</span>
                            Give Feedback
                          </button>
                        </div>
                      )}

                      {tx.status === "pending" && user?.id === tx.buyer_id && (
                        <div className="pending-message">
                          <span className="pending-icon">⏳</span>
                          Waiting for seller confirmation
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* SUCCESS POP-UP */}
{showSuccessModal && (
  <div className="modal-overlay">
    <div className="success-modal">
      <div className="success-icon">✓</div>
      <h3>Feedback submitted successfully!</h3>
      <button
        onClick={() => setShowSuccessModal(false)}
        className="return-btn"
      >
        Return
      </button>
    </div>
  </div>
)}

{/* ERROR POP-UP */}
{showErrorModal && (
  <div className="modal-overlay">
    <div className="success-modal">
      <div className="error-icon">!</div>
      <h3>{errorMessage}</h3>
      <button
        onClick={() => setShowErrorModal(false)}
        className="return-btn"
      >
        Return
      </button>
    </div>
  </div>
)}

    </>
  )
}

export default Transactions
